import { useAuth0 } from '@auth0/auth0-react';
import { useAvailableAccounts } from '@my/directory-api/hooks';
import { useAccount } from '@my/platform/account';
import { AccountSwitcher, Sidebar, type SidebarItem } from '@my/ui';
import { ClipboardCheck, LayoutDashboard, ListChecks, Settings, ShieldCheck } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getCurrentSafetyRole } from '@/auth/currentRole';
import { useUiStore } from '@/stores/ui';

function getDisplayName(user: ReturnType<typeof useAuth0>['user']): string {
  if (!user) return 'Demo User';
  const given = user.given_name ?? '';
  const family = user.family_name ?? '';
  const full = `${given} ${family}`.trim();
  return full || user.nickname || user.email || 'Demo User';
}

/**
 * Safety-flavoured `<Sidebar>` from `@my/ui`. Holds the Safety menu config and
 * bridges Auth0 → Sidebar (user, sign-out). This is intentionally a slim
 * skeleton nav for walk-around checks — add items here as Safety features land.
 *
 * Account switching: composes `useAvailableAccounts` (directory-api),
 * `useAccount` (platform), and `<AccountSwitcher>` (ui) into the Sidebar's
 * `headerSlot`. Switching an account updates the platform store and refreshes
 * the Auth0 access token bound to the new `account_id`.
 */
export function AppSidebar() {
  const { user, logout, isAuthenticated, getAccessTokenSilently } = useAuth0();
  const { currentAccountId, setCurrentAccountId } = useAccount();
  const isOpen = useUiStore((s) => s.isSidebarOpen);
  const toggle = useUiStore((s) => s.toggleSidebar);
  const { t } = useTranslation('navigation');

  const accountsQuery = useAvailableAccounts({ enabled: isAuthenticated });
  const accounts = useMemo(
    () => (accountsQuery.data ?? []).map((a) => ({ id: a.id, name: a.name })),
    [accountsQuery.data],
  );

  const handleSwitch = async (accountId: string) => {
    setCurrentAccountId(accountId);
    // Re-mint the access token bound to the newly selected account.
    // `cacheMode: 'off'` forces Auth0 to round-trip rather than returning the
    // previous token from cache.
    await getAccessTokenSilently({
      authorizationParams: {
        account_id: accountId,
        scope: 'openid profile email offline_access',
        subscription: 'safety',
      },
      cacheMode: 'off',
    });
  };

  // Settings access is gated on the **Safety app role**, not the account
  // role. The pages under Settings are app-specific, so a user who's
  // promoted to Safety Admin while still being an account-level Member
  // should see them. Mirrors `RequireRole`'s gate.
  const role = getCurrentSafetyRole(user);
  const canSeeSettings = role === 'owner' || role === 'admin';

  const items: SidebarItem[] = [
    {
      id: 'dashboard',
      label: t('dashboard'),
      icon: LayoutDashboard,
      href: '/dashboard',
      matchRoot: true,
    },
    {
      id: 'checks',
      label: t('checks'),
      icon: ClipboardCheck,
      href: '/checks',
    },
    {
      id: 'templates',
      label: t('templates'),
      icon: ListChecks,
      href: '/templates',
    },
    {
      id: 'settings',
      label: t('settings'),
      icon: Settings,
      hidden: !canSeeSettings,
      children: [
        {
          id: 'organisation',
          label: t('organisation'),
          href: '/settings/organisation',
        },
        {
          id: 'usersAndAccess',
          label: t('usersAndAccess'),
          href: '/settings/users-and-access',
        },
      ],
    },
  ];

  return (
    <Sidebar
      isOpen={isOpen}
      onToggle={toggle}
      brand={{ title: 'My Safety', icon: ShieldCheck }}
      headerSlot={
        <AccountSwitcher
          accounts={accounts}
          currentAccountId={currentAccountId}
          onSwitch={handleSwitch}
          isLoading={accountsQuery.isPending}
        />
      }
      items={items}
      user={{ displayName: getDisplayName(user), role }}
      onSignOut={() => logout({ logoutParams: { returnTo: window.location.origin } })}
    />
  );
}
